import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { STATUS_STYLES, formatDateLong, waLink, renderTemplate, DEFAULT_TEMPLATES } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BellRinging, WhatsappLogo, CheckCircle, Clock, PaperPlaneTilt, ArrowRight, SkipForward } from "@phosphor-icons/react";

function lokasiLabel(l = "") {
  return l.toLowerCase().includes("homecare") ? "Homecare" : "Gerai GBS";
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Makassar", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function Reminders() {
  const [items, setItems] = useState([]);
  const [tanggal, setTanggal] = useState("");
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState(null);
  const [qIdx, setQIdx] = useState(0);
  const [settings, setSettings] = useState(null);
  const [auto, setAuto] = useState(null);
  useEffect(() => { api.get("/reminders/auto-config").then((r) => setAuto(r.data)).catch(() => {}); }, []);
  const saveAuto = async (next) => {
    try { const r = await api.put("/reminders/auto-config", next); setAuto(r.data); toast.success("Pengaturan pengingat otomatis disimpan"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const buildMsg = (r) =>
    renderTemplate((settings && settings.tpl_reminder) || DEFAULT_TEMPLATES.tpl_reminder, {
      nama: r.nama_lengkap,
      tanggal: formatDateLong(r.tanggal_kunjungan),
      jam: r.jam_kunjungan,
      layanan: r.jenis_layanan,
      petugas: r.petugas_nama || "-",
      lokasi: lokasiLabel(r.lokasi),
    });

  const load = () => {
    setLoading(true);
    api.get("/reminders/tomorrow").then((r) => { setItems(r.data.items); setTanggal(r.data.tanggal); })
      .catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => setSettings({})); }, []);

  const pending = items.filter((x) => !x.reminder_sent);

  const sendWA = (r) => { window.open(waLink(r.no_whatsapp, buildMsg(r)), "_blank"); };

  const markSent = async (r) => {
    try { await api.post(`/reservations/${r.id}/reminder-sent`); toast.success("Ditandai sudah dikirim"); setItems((p) => p.map((x) => x.id === r.id ? { ...x, reminder_sent: true } : x)); }
    catch (e) { toast.error(apiError(e)); }
  };

  // ---- Bulk queue (Kirim Semua) ----
  const startQueue = () => {
    if (pending.length === 0) return toast.info("Semua pasien sudah diingatkan");
    setQueue(pending); setQIdx(0);
  };
  const current = queue ? queue[qIdx] : null;
  const advance = () => {
    if (!queue) return;
    if (qIdx + 1 >= queue.length) { setQueue(null); setQIdx(0); toast.success("Selesai mengirim semua pengingat"); }
    else setQIdx(qIdx + 1);
  };
  const sendAndNext = async () => {
    const r = current;
    sendWA(r);
    try { await api.post(`/reservations/${r.id}/reminder-sent`); } catch { /* ignore */ }
    setItems((p) => p.map((x) => x.id === r.id ? { ...x, reminder_sent: true } : x));
    advance();
  };

  return (
    <div className="animate-fade-up space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><BellRinging size={26} weight="duotone" className="text-[#7C4DE0]" /><h1 className="font-heading text-3xl font-extrabold text-slate-800">Pengingat H-1</h1></div>
          <p className="mt-1 text-sm text-slate-500">Reservasi besok{tanggal ? ` · ${formatDateLong(tanggal)}` : ""} · {items.length} pasien · {pending.length} belum diingatkan</p>
        </div>
        {pending.length > 0 && (
          <button data-testid="send-all-reminders" onClick={startQueue} className="flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-600 active:scale-95"><PaperPlaneTilt size={16} weight="fill" /> Kirim Semua Pengingat ({pending.length})</button>
        )}
      </div>

      {auto && (
        <div data-testid="reminder-auto-panel" className="flex flex-wrap items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/50 p-3 text-sm">
          <label className="flex items-center gap-2 font-semibold text-slate-700">
            <input data-testid="reminder-auto-toggle" type="checkbox" checked={!!auto.enabled} onChange={(e) => saveAuto({ ...auto, enabled: e.target.checked })} /> Pengingat otomatis
          </label>
          <span className="text-slate-500">Jam kirim</span>
          <select data-testid="reminder-auto-time" value={auto.send_time} onChange={(e) => saveAuto({ ...auto, send_time: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
            {Array.from({ length: 28 }, (_, i) => `${String(6 + Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`).map((t) => <option key={t} value={t}>{t} WITA</option>)}
          </select>
          <span className="text-xs text-slate-500">Hanya reservasi Dikonfirmasi · 1 pesan per pasien · cek ulang tiap 30 menit s/d 20:00</span>
        </div>
      )}

      {loading ? <p className="text-sm text-slate-400">Memuat...</p> : items.length === 0 ? (
        <div data-testid="reminders-empty" className="rounded-2xl border border-dashed border-rose-200 bg-white p-10 text-center text-slate-400">
          <CheckCircle size={40} weight="duotone" className="mx-auto text-emerald-400" />
          <p className="mt-2 text-sm">Tidak ada reservasi untuk besok.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {items.map((r) => (
            <div key={r.id} data-testid={`reminder-${r.id}`} className="rounded-2xl border border-rose-100/70 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-heading font-bold text-slate-800">{r.nama_lengkap}</p>
                  <p className="text-xs text-slate-500">{r.no_whatsapp}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${STATUS_STYLES[r.status] || ""}`}>{r.status}</span>
                  {r.reminder_sent && <span data-testid={`reminder-sent-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700"><CheckCircle size={12} weight="bold" /> Reminder sudah dikirim</span>}
                  {r.hadir_confirmed && <span data-testid={`hadir-confirmed-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-bold text-sky-700"><CheckCircle size={12} weight="bold" /> Konfirmasi hadir</span>}
                </div>
              </div>
              <div className="mt-2 space-y-1 text-sm text-slate-600">
                <p>🌷 {r.jenis_layanan}</p>
                <p className="flex items-center gap-1"><Clock size={14} /> {formatDateLong(r.tanggal_kunjungan)} · {r.jam_kunjungan}</p>
                <p>👩‍⚕️ {r.petugas_nama || "-"} · 📍 {lokasiLabel(r.lokasi)}</p>
                {r.reminder_sent && (r.reminder_sent_at || r.reminder_sent_by) && (
                  <p data-testid={`reminder-history-${r.id}`} className="mt-1 rounded-lg bg-emerald-50 px-2 py-1 text-xs text-emerald-700">
                    Dikirim {fmtDateTime(r.reminder_sent_at)}{r.reminder_sent_by ? ` oleh ${r.reminder_sent_by}` : ""}
                  </p>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button data-testid={`send-reminder-${r.id}`} onClick={() => sendWA(r)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-600"><WhatsappLogo size={14} weight="fill" /> Kirim Pengingat WhatsApp</button>
                {!r.reminder_sent && <button data-testid={`mark-sent-${r.id}`} onClick={() => markSent(r)} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600"><CheckCircle size={14} /> Tandai Sudah Dikirim</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bulk send queue modal */}
      <Dialog open={!!queue} onOpenChange={(v) => !v && setQueue(null)}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="send-all-dialog">
          <DialogHeader><DialogTitle className="font-heading">Kirim Pengingat ({qIdx + 1} dari {queue?.length || 0})</DialogTitle></DialogHeader>
          {current && (
            <div className="space-y-4">
              <div className="rounded-2xl bg-[#F7F0FF] p-4">
                <p className="font-heading font-bold text-slate-800">{current.nama_lengkap}</p>
                <p className="text-xs text-slate-500">{current.no_whatsapp}</p>
                <div className="mt-2 space-y-1 text-sm text-slate-600">
                  <p>🌷 {current.jenis_layanan}</p>
                  <p className="flex items-center gap-1"><Clock size={14} /> {formatDateLong(current.tanggal_kunjungan)} · {current.jam_kunjungan}</p>
                  <p>👩‍⚕️ {current.petugas_nama || "-"} · 📍 {lokasiLabel(current.lokasi)}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button data-testid="queue-send-next" onClick={sendAndNext} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-600"><WhatsappLogo size={16} weight="fill" /> Kirim & Lanjut <ArrowRight size={14} weight="bold" /></button>
                <button data-testid="queue-skip" onClick={advance} className="flex items-center justify-center gap-1 rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-600"><SkipForward size={14} /> Lewati</button>
              </div>
              <p className="text-center text-xs text-slate-400">WhatsApp akan terbuka, tekan Kirim di WhatsApp lalu kembali ke sini.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
